/**
 * ForRoofersPage
 * /for-roofers
 *
 * Audience-specific page for four segments:
 * - Solo/Small Teams
 * - Insurance/Storm Restoration
 * - Retail/Commercial
 * - Agencies
 *
 * Uses real content from the content brief and specific outcomes.
 */

'use client';

import { useState } from 'react';
import { useReducedMotion, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Link } from 'react-router-dom';
import { AnimatedSection, StaggerContainer, StaggerItem } from '@/components/marketing';
import { cn } from '@/lib/utils';
import { getAuthLink } from '@/lib/links';
import { useSanityPage } from '@/lib/sanity.hooks';
import {
  ArrowRight,
  Sparkle,
  Buildings,
  House,
  ShieldCheck,
  Users,
  CheckCircle,
  FileText,
  Calculator,
  MapPin,
  ChartBar,
  Clock,
  CurrencyDollar,
  Lightning,
  Target,
  CloudRain,
  Handshake,
  ChartLineUp,
} from '@phosphor-icons/react';

const rooferTypes = [
  {
    id: 'solo',
    icon: Sparkle,
    name: 'Solo & Small Teams',
    subtitle: '1-10 employees · $500K - $5M revenue',
    color: 'gold',
    description:
      'You are the sales rep, the estimator, and the project manager. You need software that saves hours without adding overhead.',
    businessValue:
      'Start selling in hours, not weeks. Reduce proposal time from hours to minutes. Capture leads that currently fall through the cracks.',
    painPoints: [
      'Leads fall through cracks between tools',
      'Manual data entry causes errors',
      'No visibility into marketing ROI',
      'Chasing payments and reconciling QuickBooks manually',
    ],
    solutions: [
      { icon: FileText, text: 'Pre-loaded proposal templates so you start selling today' },
      { icon: Calculator, text: 'Free ArcGIS roof estimates for instant website quotes' },
      { icon: Lightning, text: 'Automated email and SMS follow-up sequences' },
      { icon: CurrencyDollar, text: 'Built-in invoicing and Stripe payment collection' },
      { icon: ChartBar, text: 'Simple CRM that scales when you hire' },
    ],
    preLoaded: [
      '12+ proposal templates',
      '200+ line items and bundles',
      'Email drip campaigns',
      'Lead nurturing sequences',
      'Invoice templates',
    ],
  },
  {
    id: 'storm',
    icon: ShieldCheck,
    name: 'Insurance & Storm',
    subtitle: 'Hail, wind, and claims specialists',
    color: 'gold',
    description:
      'Storm work is time-sensitive. You need to identify damage fast, document everything, and move claims from adjuster to signed contract.',
    businessValue:
      'Target storm-damaged areas within 24 hours. Track claims and supplements in one place. Improve adjuster communication with centralized documentation.',
    painPoints: [
      'Supplement tracking is a nightmare',
      'Adjuster communications get lost',
      'Need storm damage documentation',
      'Canvassing routes are inefficient',
    ],
    solutions: [
      { icon: CloudRain, text: 'Storm tracking and territory alerts by zip code' },
      { icon: FileText, text: 'Claim tracking and supplement workflows' },
      { icon: Users, text: 'Adjuster communication logs tied to the job file' },
      { icon: MapPin, text: 'Canvassing hub with route planning' },
      { icon: ChartBar, text: 'Insurance-specific reporting and stage tracking' },
    ],
    preLoaded: [
      'Insurance scope templates',
      'Supplement request templates',
      'Adjuster contact forms',
      'Damage documentation checklists',
      'Claim status workflows',
    ],
  },
  {
    id: 'retail',
    icon: House,
    name: 'Retail & Commercial',
    subtitle: 'Homeowner and building owner focus',
    color: 'gold',
    description:
      'Retail and commercial jobs depend on speed, professionalism, and profitability. You need instant estimates, strong proposals, and real-time job costing.',
    businessValue:
      'Close deals faster with instant estimates and digital signing. Track job profitability in real time. Reduce wasted trips and material overruns.',
    painPoints: [
      'Homeowners expect instant quotes',
      'Competing with lowball contractors',
      'Progress billing is manual and error-prone',
      'Material ordering for large jobs is complex',
    ],
    solutions: [
      { icon: Calculator, text: 'Instant satellite estimates from any address' },
      { icon: FileText, text: 'Good/Better/Best tiered proposals' },
      { icon: CurrencyDollar, text: 'Online payment collection and deposit tracking' },
      { icon: Clock, text: 'Progress billing and retention tracking' },
      { icon: ChartBar, text: 'Real-time job profitability dashboard' },
    ],
    preLoaded: [
      'Residential and commercial proposal templates',
      'Shingle and roof type catalogs',
      'Financing offer templates',
      'Review request sequences',
      'Homeowner follow-up campaigns',
    ],
  },
  {
    id: 'agencies',
    icon: Buildings,
    name: 'Agencies',
    subtitle: 'Roofing marketing firms and lead generators',
    color: 'gold',
    description:
      'You generate leads for contractor clients. You need proof that your campaigns close, not just that they convert.',
    businessValue:
      'Track lead-to-close for every client. Prove ROI with attribution dashboards. Deliver white-label reporting that makes retention easy.',
    painPoints: [
      'No way to track lead-to-close for clients',
      'Manual reporting on campaign performance',
      'Difficulty proving ROI to contractor clients',
      'Leads handed off without follow-up visibility',
    ],
    solutions: [
      { icon: Target, text: 'Attribution tracking by source, campaign, and ad group' },
      { icon: ChartLineUp, text: 'Client dashboards with funnel and revenue metrics' },
      { icon: FileText, text: 'White-label reporting exports' },
      { icon: Handshake, text: 'Multi-tenant client workspaces with Row Level Security' },
      { icon: Lightning, text: 'Automated re-nurture sequences for stalled leads' },
    ],
    preLoaded: [
      'Campaign attribution templates',
      'Client performance dashboards',
      'White-label report layouts',
      'Lead source tracking widgets',
      'Re-nurture automation playbooks',
    ],
  },
];

function Reveal({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export const ForRoofersPage = () => {
  const { data: cmsData } = useSanityPage('for-roofers');
  const [activeType, setActiveType] = useState('solo');
  const selectedType = rooferTypes.find((t) => t.id === activeType)!;

  const pageTitle = cmsData?.seo?.metaTitle || 'Roofing Business Management Software for Roofers';
  const pageDescription =
    cmsData?.seo?.metaDescription ||
    cmsData?.description ||
    'Rooflyzer OS gives roofers a complete business management platform: leads, estimates, proposals, invoices, and team management, all mobile-first.';
  const heroTitle = cmsData?.title || 'Solutions for every roofer.';
  const heroSubtitle =
    cmsData?.description ||
    'Select your business profile and see exactly how Rooflyzer OS is configured to solve your specific challenges.';

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl="/for-roofers"
        keywords="roofing business software, roofing management app, roofer CRM, roofing contractor software"
        ogImage={cmsData?.seo?.ogImage?.asset?.url || '/social-card.svg'}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rooflyzer.pro' },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'For Roofers',
              item: 'https://rooflyzer.pro/for-roofers',
            },
          ],
        }}
      />

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="relative pt-28 pb-16 px-4 sm:px-6 text-center overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider mb-6">
              <Users className="w-4 h-4 text-emerald-400" weight="fill" />
              Built for you
            </div>

            <h1 className="hero-h1 font-bold text-white mb-5">
              {cmsData?.title ? (
                heroTitle
              ) : (
                <>
                  Solutions for <span className="text-white">every roofer.</span>
                </>
              )}
            </h1>

            <p className="hero-lead text-white/65 max-w-2xl mx-auto mb-4">{heroSubtitle}</p>
            <p className="body-lg text-white/50 max-w-2xl mx-auto">
              Every configuration is pre-loaded with workflows and templates designed for your
              business model.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* ── Product Screenshot ────────────────────────────────────── */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden bg-slate-900/60 border border-slate-800">
              <div className="absolute inset-0 rounded-3xl border border-white/[0.06] z-20 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent z-10" />
              <img
                src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?q=80&w=2400&auto=format&fit=crop"
                alt="Aerial view of suburban rooftops for roofing contractor targeting and estimates"
                className="w-full h-[360px] sm:h-[480px] object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-6 left-6 z-20 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                <span className="px-3 py-1 bg-slate-900/80 border border-slate-700/80 rounded-full text-sm text-slate-200">
                  Target the right homes, not just any home
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Type Selector Tabs ────────────────────────────────────── */}
      <section className="relative z-10 max-w-5xl mx-auto py-8 px-4">
        <div className="flex flex-wrap justify-center gap-2">
          {rooferTypes.map((type) => {
            const isActive = activeType === type.id;
            return (
              <button
                key={type.id}
                onClick={() => setActiveType(type.id)}
                className={cn(
                  'flex items-center gap-2 px-5 py-3 rounded-lg border transition-all duration-300',
                  isActive
                    ? 'bg-white text-slate-950 font-semibold border-transparent shadow-sm'
                    : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:text-white hover:bg-slate-800/60'
                )}
              >
                <type.icon className="w-4 h-4" weight={isActive ? 'fill' : 'regular'} />
                <span className="text-sm">{type.name}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Selected Type Details ─────────────────────────────────── */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            key={selectedType.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="bg-slate-900/60 rounded-3xl p-8 md:p-12 border border-slate-800"
          >
            {/* Detail Header */}
            <div className="flex flex-col md:flex-row items-start gap-6 mb-10">
              <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700/80 flex items-center justify-center">
                <selectedType.icon className="w-8 h-8 text-emerald-400" weight="fill" />
              </div>
              <div className="flex-1">
                <span className="text-xs font-mono font-semibold uppercase tracking-wider text-slate-400 block mb-1">
                  {selectedType.subtitle}
                </span>
                <h2 className="text-3xl font-bold text-white mb-3">{selectedType.name}</h2>
                <p className="body-lg text-white/70 leading-relaxed mb-4">
                  {selectedType.description}
                </p>
                {selectedType.businessValue && (
                  <div className="bg-slate-800/60 rounded-xl p-3 max-w-2xl border border-slate-700/60">
                    <p className="text-slate-300 text-sm font-medium leading-relaxed">
                      {selectedType.businessValue}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Pain Points & Solutions */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-emerald-400" weight="fill" />
                    Your core challenges
                  </h3>
                  <ul className="space-y-3 pl-1">
                    {selectedType.painPoints.map((point, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-white/75">
                        <span className="text-[#f87171] font-bold mt-0.5">•</span>
                        <span className="body-lg leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Lightning className="w-5 h-5 text-emerald-400" weight="fill" />
                    How we solve them
                  </h3>
                  <ul className="space-y-4">
                    {selectedType.solutions.map((solution, i) => (
                      <li key={i} className="flex items-start gap-3.5 text-white/80">
                        <div className="w-9 h-9 rounded-xl bg-slate-800 border border-slate-700/80 flex items-center justify-center flex-shrink-0">
                          <solution.icon className="w-4 h-4 text-emerald-400" weight="fill" />
                        </div>
                        <span className="body-lg leading-relaxed">{solution.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Pre-loaded */}
              <div className="space-y-8">
                <div className="bg-slate-900/40 rounded-2xl p-6 border border-slate-800/80">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" weight="fill" />
                    Pre-loaded for {selectedType.name}
                  </h3>
                  <ul className="space-y-3">
                    {selectedType.preLoaded.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-white/70 text-sm">
                        <CheckCircle
                          className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0"
                          weight="fill"
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-slate-900/80 rounded-2xl p-6 border border-emerald-500/30">
                  <h3 className="text-lg font-bold text-white mb-2">Ready when you are</h3>
                  <p className="text-white/70 text-sm leading-relaxed mb-4">
                    Start with the free tier. Add users, territories, and integrations as you grow.
                    No forced migration to a different plan.
                  </p>
                  <a
                    href={getAuthLink('/register')}
                    className="inline-flex items-center text-emerald-400 text-sm font-semibold hover:underline"
                  >
                    Start free
                    <ArrowRight className="ml-1.5 size-4" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Cross-segment proof ─────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-14">
            <h2 className="section-h2 font-bold text-white mb-3">What every segment gets</h2>
            <p className="body-lg text-white/55">
              Core capabilities that do not change no matter how you run your business.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: Target,
                title: 'Attribution',
                desc: 'Track every lead from source to signed contract.',
              },
              {
                icon: FileText,
                title: 'Proposals',
                desc: 'Good/Better/Best pricing with digital signatures.',
              },
              {
                icon: CurrencyDollar,
                title: 'Payments',
                desc: 'Stripe, deposits, and automated reminders.',
              },
              {
                icon: CloudRain,
                title: 'Storm intel',
                desc: 'Zip-code targeting and canvassing routes.',
              },
            ].map((item, i) => (
              <StaggerItem key={i}>
                <div className="bg-slate-900/60 rounded-2xl p-6 border border-slate-800 hover:border-slate-700 transition-all duration-300 h-full">
                  <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700/80 flex items-center justify-center mb-4 text-emerald-400">
                    <item.icon className="w-5 h-5" weight="fill" />
                  </div>
                  <h3 className="font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-white/55 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────── */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <AnimatedSection>
            <div className="bg-slate-900/80 rounded-3xl p-10 md:p-12 border border-slate-800">
              <h2 className="section-h2 font-bold text-white mb-4">
                See your workflow in Rooflyzer
              </h2>
              <p className="text-white/55 body-lg mb-8">
                Tell us which segment you fit and we will show you a demo configured for your actual
                business.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/demo">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 rounded-lg shadow-sm"
                  >
                    Book a demo
                    <ArrowRight className="ml-2 size-5" />
                  </Button>
                </Link>
                <a
                  href={getAuthLink('/register')}
                  className="inline-flex items-center justify-center w-full sm:w-auto border border-slate-700 bg-slate-800/60 text-slate-200 hover:text-white hover:bg-slate-700/60 rounded-lg px-8 py-3 text-sm font-medium transition-colors"
                >
                  Start free
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};
